import React from 'react';
import { Switch, Tooltip, Typography, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import classNames from 'classnames';
import styles from './AutomationItem.module.css';

/**
 * Item de automação de mensagem (Preferências > Automação).
 *
 * @param {object} item - Definição do catálogo (ver automationsCatalog.js): { type, title, description, schedule, audience, conditions, icon }.
 * @param {boolean} checked - Se a automação está ligada.
 * @param {boolean} disabled - Se o switch está desabilitado (fora do modo de edição, WhatsApp indisponível, trial ou plano).
 * @param {boolean} blocked - Se a automação está indisponível agora (mostra ícone de info + tooltip).
 * @param {string} tooltip - Mensagem do tooltip quando blocked.
 * @param {string} [note] - Texto informativo (não bloqueia o switch), ex.: limitação de plano nos reenvios de confirmação.
 * @param {function} onToggle - Callback ao clicar no switch.
 */
const AutomationItem = ({ item, checked, disabled, blocked, tooltip, note, onToggle }) => {
    const Icon = item.icon;
    const AudienceIcon = item.audience === 'Paciente' ? PersonOutlineIcon : GroupsOutlinedIcon;

    const card = (
        <div className={styles.card}>
            <div className={styles.iconBadge}>
                <Icon className={styles.icon} />
            </div>

            <div className={classNames(styles.content, { [styles.contentDimmed]: !checked })}>
                <div className={styles.titleRow}>
                    <Typography className={styles.title}>{item.title}</Typography>
                    {blocked && (
                        <InfoOutlinedIcon
                            color="action"
                            fontSize="small"
                            className={styles.infoIcon}
                        />
                    )}
                </div>

                <Typography className={styles.description}>{item.description}</Typography>

                {note && (
                    <div className={styles.note}>
                        <InfoOutlinedIcon fontSize="inherit" className={styles.noteIcon} />
                        <Typography variant="caption" className={styles.noteText}>{note}</Typography>
                    </div>
                )}

                <div className={styles.metaRow}>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<AccessTimeOutlinedIcon />}
                        label={item.schedule}
                        className={styles.chip}
                    />
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<AudienceIcon />}
                        label={item.audience}
                        className={styles.chip}
                    />
                </div>

                <div className={styles.conditions}>
                    <Typography className={styles.conditionsLabel}>Enviado quando:</Typography>
                    <ul className={styles.conditionsList}>
                        {item.conditions.map((condition, index) => (
                            <li key={index} className={styles.conditionItem}>{condition}</li>
                        ))}
                    </ul>
                </div>
            </div>

            <Switch
                checked={checked}
                onChange={onToggle}
                disabled={disabled}
                color="secondary"
                className={styles.switch}
            />
        </div>
    );

    if (!blocked) {
        return card;
    }

    return (
        <Tooltip title={tooltip || ''} placement="top">
            <div>{card}</div>
        </Tooltip>
    );
};

export default AutomationItem;
